import { useCallback, useMemo } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  buildConceptGroupMaps,
  buildDefinitionFromStore,
  type ConceptGroupOption,
  useCohortDefinitionStore,
} from '@verantos-dev/feature-analytical-variables';
import type { AnalyticalVariable } from '@verantos-dev/types-cohorts';

import { useAVConceptGroupSelectionSync } from './useAVConceptGroupSelectionSync';
import { useEmitResearchAssistantUpdate } from '../../hooks/common/useEmitResearchAssistantUpdate';

interface AVAutoSavePipelineInputs {
  analyticalVariable: AnalyticalVariable | undefined;
  /** Optional — when present, AV concept_ref edits push a sync upsert (OR-5840). */
  cohortId?: string | undefined;
  /** Optional — used to resolve concept group ids to display names on the sync payload. */
  conceptGroups?: ConceptGroupOption[] | undefined;
}

/**
 * Returns a callback that optimistically writes AV updates to Zustand
 * and emits the full cohort definition via socket for BE persistence.
 *
 * Reads from Zustand (not Redux) so the pipeline works on both the
 * Cohort Builder page and the standalone AV edit page after refresh.
 */
export const useAVAutoSavePipeline = ({
  analyticalVariable,
  cohortId,
  conceptGroups,
}: AVAutoSavePipelineInputs) => {
  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate();

  // Gate AV concept_ref sync behind the same flag that gates the workflow-side
  // mutation-driven selection behavior. Both ship and roll back as a unit so the
  // persisted-state rescue and its planner-driven bypass arrive together.
  // TODO: cleanup with gate_concept_group_selection_on_mutations ff
  const isMutationGatingEnabled = useFeatureFlagSplit('gate_concept_group_selection_on_mutations');

  const conceptGroupIdToNameMap = useMemo(
    () => buildConceptGroupMaps(conceptGroups).idToNameMap,
    [conceptGroups]
  );
  const syncConceptGroupSelections = useAVConceptGroupSelectionSync({
    cohortId: isMutationGatingEnabled ? cohortId : undefined,
    conceptGroupIdToNameMap,
  });

  return useCallback(
    (updates: Partial<AnalyticalVariable>) => {
      if (!analyticalVariable) return;

      // Optimistically update the AV in Zustand
      const store = useCohortDefinitionStore.getState();
      store.updateAnalyticalVariable(analyticalVariable.id, updates);
      // Editing a library-linked variable creates a local copy — sever the library link.
      store.updateAvCriterionSourceLibraryAvId(analyticalVariable.id, null);

      // Editing the variable clears any outstanding warning chips/icons for it.
      // Error chips/icons clear separately when the AV's logic resolves to a valid CG —
      // see useGetAVCriteriaStatus error-resolution effect.
      if (analyticalVariable.name) {
        useCohortDefinitionStore.getState().markConceptGroupSelectionsAsReported({
          containerName: analyticalVariable.name,
          statuses: ['warning'],
        });
      }

      // Diff concept_ref selections before/after this update and push any changes to
      // the persisted concept group selection state. This is the rescue path for
      // OR-5840: the next chat turn's apply step will re-apply these refIds even if
      // the AV regen LLM drops or rewrites them.
      const nextAv: AnalyticalVariable = { ...analyticalVariable, ...updates };
      syncConceptGroupSelections(analyticalVariable, nextAv);

      // Build cohort definition from Zustand (the source of truth).
      // Reading fresh state after the updates above ensures we include them.
      emitResearchAssistantUpdate(buildDefinitionFromStore());
    },
    [analyticalVariable, emitResearchAssistantUpdate, syncConceptGroupSelections]
  );
};
